import React, { useState, useEffect, useCallback } from 'react';
import { 
  Twitter, RefreshCw, Check, X, Eye, ChevronRight, 
  AlertCircle, Clock, TrendingUp, ExternalLink, 
  FileText, Zap, Plus, Trash2, ToggleLeft, ToggleRight,
  Activity, Filter, Loader2, Edit3, RotateCcw, Bell
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import * as api from '../../services/api';

// ============================================
// X MONITOR MANAGER
// Real-time news monitoring from Twitter/X
// With generation progress tracking
// ============================================

const XMonitorManager = () => {
  // State
  const [activeView, setActiveView] = useState('approvals');
  const [approvals, setApprovals] = useState([]);
  const [stories, setStories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  
  // Story modal state
  const [selectedStory, setSelectedStory] = useState(null);
  const [editingStory, setEditingStory] = useState(false);
  const [storyFormData, setStoryFormData] = useState({});
  const [savingStory, setSavingStory] = useState(false);
  const [retryingStoryId, setRetryingStoryId] = useState(null);
  const [deletingStoryId, setDeletingStoryId] = useState(null);
  
  // Generation tracking state
  const [generatingItems, setGeneratingItems] = useState([]);
  const [activeGenerations, setActiveGenerations] = useState(0);
  // Ticking clock (ms) so the "En Redacción" timer counts up smoothly every
  // second, independent of the 3s server poll. Computed from each item's
  // created_at instead of relying on the static backend elapsed_seconds.
  const [nowTs, setNowTs] = useState(() => Date.now());
  
  // High relevance notifications state
  const [highRelevanceCount, setHighRelevanceCount] = useState(0);
  const [highRelevanceItems, setHighRelevanceItems] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  
  // Multi-select state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Account management state
  const [newAccount, setNewAccount] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);
  
  // Scheduler control state
  const [scrapingActive, setScrapingActive] = useState(true);
  const [scrapingInterval, setScrapingInterval] = useState(30);
  const [nextScrapingTime, setNextScrapingTime] = useState(null);
  const [changingInterval, setChangingInterval] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    username: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Helper: Check if a story is in a failed state
  const isStoryFailed = (story) => {
    // Check explicit failure flags first
    if (story.regeneration_failed === true) return true;
    if (story.status === 'ERROR' || story.status === 'FAILED' || story.status === 'GENERATION_FAILED') return true;
    
    const content = (story.content || story.summary || '').toLowerCase();
    
    // Check if content is too short (less than 100 chars means generation failed)
    if (content.length < 100) return true;
    
    // Check for specific error phrases (not just the word "error")
    const errorPhrases = [
      'fuentes insuficientes',
      'no se pudo generar',
      'clasificada como tipo c',
      'error en la generación',
      'artículo en desarrollo',
      'error al generar',
      'generación fallida'
    ];
    
    return errorPhrases.some(phrase => content.includes(phrase));
  };

  // Handle retry story generation
  const handleRetryStory = async (storyId) => {
    setRetryingStoryId(storyId);
    try {
      const result = await api.retryStoryGeneration(storyId);
      setRunResult({
        success: true,
        message: '🔄 Regeneración iniciada. El artículo se generará en segundo plano.',
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
      
      // Switch to "En Redacción" view to see progress
      setActiveView('redaccion');
      
      // Refresh data after a moment
      setTimeout(() => {
        fetchData();
        fetchGenerating();
      }, 1000);
    } catch (error) {
      console.error('Error retrying story:', error);
      setRunResult({
        success: false,
        message: `❌ Error: ${error.response?.data?.detail || error.message}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
    } finally {
      setRetryingStoryId(null);
    }
  };

  // Handle delete story
  const handleDeleteStory = async (storyId, storyTitle) => {
    const confirmed = window.confirm(
      `¿Estás seguro de que deseas eliminar esta historia?\n\n"${storyTitle || 'Sin título'}"\n\nEsta acción no se puede deshacer.`
    );
    
    if (!confirmed) return;
    
    setDeletingStoryId(storyId);
    try {
      await api.deleteStory(storyId);
      
      // Remove from local state immediately
      setStories(prev => prev.filter(s => s.story_id !== storyId));
      
      setRunResult({
        success: true,
        message: '🗑️ Historia eliminada correctamente.',
        details: null
      });
      setTimeout(() => setRunResult(null), 3000);
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Error deleting story:', error);
      setRunResult({
        success: false,
        message: `❌ Error al eliminar: ${error.response?.data?.detail || error.message}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
    } finally {
      setDeletingStoryId(null);
    }
  };


  // Fetch generating items (articles in progress)
  const fetchGenerating = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pending-approvals/generating`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratingItems(data.generating || []);
        setActiveGenerations(data.active_generations || 0);
      }
    } catch (error) {
      console.error('Error fetching generating:', error);
    }
  }, []);

  // Fetch high relevance notifications (score > 50)
  const fetchHighRelevance = useCallback(async () => {
    try {
      const data = await api.getHighRelevanceNotifications();
      setHighRelevanceCount(data.count || 0);
      setHighRelevanceItems(data.items || []);
    } catch (error) {
      console.error('Error fetching high relevance:', error);
    }
  }, []);

  // Fetch data - NON-BLOCKING with individual error handling
  const fetchData = useCallback(async () => {
    try {
      // Build query params with filters
      const params = new URLSearchParams({
        status: 'PENDING',
        limit: pageLimit,
        page: currentPage,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      });
      
      if (filters.username) params.append('username', filters.username);
      
      // Fetch in parallel with individual error handling
      const results = await Promise.allSettled([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pending-approvals?${params}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()),
        api.getStories('ACTIVE'),
        api.getXMonitorStatus(),
        fetchGenerating(),
        fetchHighRelevance()
      ]);
      
      // Process approvals
      if (results[0].status === 'fulfilled') {
        setApprovals(results[0].value.approvals || []);
        setPagination({
          total_count: results[0].value.total_count || 0,
          total_pages: results[0].value.total_pages || 1,
          has_next: results[0].value.has_next || false,
          has_prev: results[0].value.has_prev || false
        });
      } else {
        console.error('Error fetching approvals:', results[0].reason);
      }
      
      // Process stories
      if (results[1].status === 'fulfilled') {
        setStories(results[1].value.stories || []);
      } else {
        console.error('Error fetching stories:', results[1].reason);
      }
      
      // Process status
      if (results[2].status === 'fulfilled') {
        setStats(results[2].value);
        // Update scheduler state from status
        setScrapingActive(results[2].value.scraping_active ?? true);
        setScrapingInterval(results[2].value.monitoring_interval_minutes || 30);
        setNextScrapingTime(results[2].value.next_scraping_time);
      } else {
        console.error('Error fetching status:', results[2].reason);
      }
    } catch (error) {
      console.error('Error in fetchData:', error);
    } finally {
      setLoading(false);
    }
  }, [pageLimit, currentPage, filters, fetchGenerating, fetchHighRelevance]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    }, 30000); // 30 seconds for main data
    return () => clearInterval(interval);
  }, [fetchData]);

  // More frequent polling for generating items when in "redaccion" view or there are items
  useEffect(() => {
    if (activeView === 'redaccion' || generatingItems.length > 0 || activeGenerations > 0) {
      const progressInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchGenerating();
        }
      }, 3000); // Every 3 seconds when actively viewing or there are items generating
      return () => clearInterval(progressInterval);
    }
  }, [activeView, generatingItems.length, activeGenerations, fetchGenerating]);

  // Live 1s tick for the "En Redacción" timer/progress so it advances smoothly
  // between server polls. Only runs while there are items being generated.
  useEffect(() => {
    if (activeView !== 'redaccion' && generatingItems.length === 0) return;
    setNowTs(Date.now());
    const tick = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setNowTs(Date.now());
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [activeView, generatingItems.length]);

  // Elapsed seconds for a generating item, computed live from created_at when
  // available (falls back to the backend-provided elapsed_seconds).
  const getElapsedSeconds = useCallback((item) => {
    // Preferir generation_started_at (inicio real de la redacción) sobre created_at
    // (scrapeo del tweet). Normalizar a UTC si el string viene sin zona horaria.
    const start = item?.generation_started_at || item?.created_at;
    if (start) {
      const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(start) ? start : `${start}Z`;
      const startMs = new Date(iso).getTime();
      if (!Number.isNaN(startMs)) {
        return Math.max(0, Math.floor((nowTs - startMs) / 1000));
      }
    }
    return item?.elapsed_seconds || 0;
  }, [nowTs]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle limit change
  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    setCurrentPage(1);
    setSelectedIds([]);
  };
  
  // Multi-select handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === approvals.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(approvals.map(a => a.approval_id));
    }
  };
  
  const toggleSelect = (approvalId) => {
    setSelectedIds(prev => 
      prev.includes(approvalId) 
        ? prev.filter(id => id !== approvalId)
        : [...prev, approvalId]
    );
  };
  
  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    
    const confirmed = window.confirm(
      `¿${action === 'APPROVE' ? 'Aprobar' : 'Eliminar'} ${selectedIds.length} tweets seleccionados?\n\n` +
      (action === 'APPROVE' 
        ? 'Los artículos se generarán en segundo plano. Puedes ver el progreso en la pestaña "En Redacción".'
        : 'Esta acción no se puede deshacer.')
    );
    
    if (!confirmed) return;
    
    // Optimistic update - remove items from list IMMEDIATELY
    const itemsToProcess = approvals.filter(a => selectedIds.includes(a.approval_id));
    const itemIds = [...selectedIds];
    
    setApprovals(prev => prev.filter(a => !selectedIds.includes(a.approval_id)));
    setPagination(prev => ({
      ...prev,
      total_count: Math.max(0, prev.total_count - selectedIds.length)
    }));
    setSelectedIds([]);
    setBulkProcessing(false);
    
    // Show non-blocking feedback
    if (action === 'APPROVE') {
      setRunResult({
        success: true,
        message: `⏳ ${itemIds.length} tweets enviados a redacción. Ve a la pestaña "En Redacción" para ver el progreso.`,
        details: null
      });
      
      // Add to generating items immediately for instant feedback
      const now = new Date().toISOString();
      const placeholders = itemsToProcess.map(item => ({
        approval_id: item.approval_id,
        progress: 5,
        status: 'GENERATING',
        created_at: now,
        suggested_title: item.analysis?.headline_candidate || item.tweet_text?.substring(0, 60) || 'Preparando artículo...',
        tweet_author: item.tweet_author,
        tweet_text: item.tweet_text
      }));
      setGeneratingItems(prev => [...placeholders, ...prev]);
      
      // Switch to redaccion view
      setActiveView('redaccion');
    } else {
      setRunResult({
        success: true,
        message: `✅ ${itemIds.length} tweets rechazados.`,
        details: null
      });
    }
    
    setTimeout(() => setRunResult(null), 8000);
    
    // Fire-and-forget - NO AWAIT (non-blocking)
    api.bulkProcessApprovals(itemIds, action)
      .then(result => {
        console.log('✅ Bulk process completado:', result);
        setTimeout(() => fetchGenerating(), 1000);
      })
      .catch(error => {
        console.error('Error en bulk process:', error);
      });
  };

  // Run X Monitor
  const handleRunMonitor = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const result = await api.runXMonitor();
      setRunResult({
        success: true,
        message: `✅ Monitoreo completado`,
        details: result
      });
      fetchData();
    } catch (error) {
      setRunResult({
        success: false,
        message: '❌ Error al ejecutar monitor',
        details: error.message
      });
    } finally {
      setRunning(false);
    }
  };

  // Process single approval
  const handleProcess = async (approvalId, action) => {
    setProcessingId(approvalId);
    
    // Store the approval data before removing for generating view
    const approvalData = approvals.find(a => a.approval_id === approvalId);
    
    // Optimistic update
    setApprovals(prev => prev.filter(a => a.approval_id !== approvalId));
    setPagination(prev => ({
      ...prev,
      total_count: Math.max(0, prev.total_count - 1)
    }));
    
    if (action === 'APPROVE' && approvalData) {
      // Add to generating items immediately
      setGeneratingItems(prev => [{
        ...approvalData,
        status: 'GENERATING',
        progress: 5,
        suggested_title: approvalData.analysis?.headline_candidate || approvalData.tweet_text?.substring(0, 60) || 'Generando artículo...',
        created_at: new Date().toISOString()
      }, ...prev]);
    }
    
    try {
      await api.processApproval(approvalId, { action });
      setRunResult({
        success: true,
        message: action === 'APPROVE' ? '✅ Artículo en redacción - ve la pestaña "En Redacción"' : '✅ Tweet rechazado',
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
      
      // Refresh generating items after a moment
      if (action === 'APPROVE') {
        setTimeout(() => fetchGenerating(), 1000);
      }
    } catch (error) {
      console.error('Error processing:', error);
      fetchData(); // Reload on error
    } finally {
      setProcessingId(null);
      setSelectedApproval(null);
    }
  };

  // Add monitored account
  const handleAddAccount = async () => {
    if (!newAccount.trim()) return;
    setAddingAccount(true);
    try {
      const result = await api.addMonitoredAccount(newAccount.replace('@', '').trim(), false);
      console.log('Add account result:', result);
      setNewAccount('');
      setRunResult({
        success: true,
        message: `✅ Cuenta @${newAccount.replace('@', '')} agregada correctamente`,
        details: null
      });
      setTimeout(() => setRunResult(null), 3000);
      // Force refresh stats immediately
      const newStats = await api.getXMonitorStatus();
      console.log('New stats after add:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('Error adding account:', error);
      setRunResult({
        success: false,
        message: `❌ Error: ${error.response?.data?.detail || error.message}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
    } finally {
      setAddingAccount(false);
    }
  };

  // Remove monitored account
  const handleRemoveAccount = async (username) => {
    if (!window.confirm(`¿Eliminar @${username} de las cuentas monitoreadas?\n\nLos tweets ya descargados de esta cuenta seguirán disponibles.`)) return;
    try {
      const result = await api.removeMonitoredAccount(username);
      console.log('Remove account result:', result);
      setRunResult({
        success: true,
        message: `✅ Cuenta @${username} eliminada`,
        details: null
      });
      setTimeout(() => setRunResult(null), 3000);
      // Force refresh stats immediately
      const newStats = await api.getXMonitorStatus();
      console.log('New stats after remove:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('Error removing account:', error);
      setRunResult({
        success: false,
        message: `❌ Error: ${error.response?.data?.detail || error.message}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
    }
  };

  // Toggle auto-publish
  const handleToggleAutoPublish = async (username, currentValue) => {
    try {
      const result = await api.toggleAutoPublish(username, !currentValue);
      console.log('Toggle auto-publish result:', result);
      setRunResult({
        success: true,
        message: `✅ Auto-publicar ${!currentValue ? 'activado' : 'desactivado'} para @${username}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 3000);
      // Force refresh stats immediately
      const newStats = await api.getXMonitorStatus();
      console.log('New stats after toggle:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('Error toggling auto-publish:', error);
      setRunResult({
        success: false,
        message: `❌ Error: ${error.response?.data?.detail || error.message}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
    }
  };

  // Toggle scraping (pause/resume)
  const handleToggleScraping = async () => {
    try {
      if (scrapingActive) {
        await api.pauseScheduler();
        setScrapingActive(false);
        setRunResult({
          success: true,
          message: '⏸️ Scraping pausado',
          details: null
        });
      } else {
        await api.resumeScheduler();
        setScrapingActive(true);
        setRunResult({
          success: true,
          message: '▶️ Scraping reanudado',
          details: null
        });
      }
      setTimeout(() => setRunResult(null), 3000);
      fetchData();
    } catch (error) {
      console.error('Error toggling scraping:', error);
      setRunResult({
        success: false,
        message: `❌ Error: ${error.response?.data?.detail || error.message}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
    }
  };

  // Change scraping interval
  const handleChangeInterval = async (newInterval) => {
    setChangingInterval(true);
    try {
      await api.setSchedulerInterval(newInterval);
      setScrapingInterval(newInterval);
      setRunResult({
        success: true,
        message: `⏱️ Intervalo cambiado a ${newInterval} minutos`,
        details: null
      });
      setTimeout(() => setRunResult(null), 3000);
      fetchData();
    } catch (error) {
      console.error('Error changing interval:', error);
      setRunResult({
        success: false,
        message: `❌ Error: ${error.response?.data?.detail || error.message}`,
        details: null
      });
      setTimeout(() => setRunResult(null), 5000);
    } finally {
      setChangingInterval(false);
    }
  };

  // Format next scraping time
  const formatNextScraping = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins <= 0) return 'Ahora';
    if (diffMins < 60) return `En ${diffMins} min`;
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Progress bar color based on percentage
  const getProgressColor = (progress) => {
    if (progress < 30) return 'bg-yellow-500';
    if (progress < 70) return 'bg-blue-500';
    return 'bg-green-500';
  };

  // Get progress stage text
  const getProgressStage = (progress) => {
    if (progress < 15) return 'Iniciando análisis...';
    if (progress < 30) return 'Analizando contenido...';
    if (progress < 50) return 'Generando borrador...';
    if (progress < 70) return 'Validando contenido...';
    if (progress < 85) return 'Aplicando estilo editorial...';
    if (progress < 95) return 'Generando imagen...';
    return 'Finalizando...';
  };

  // Extract title from approval data
  const getApprovalTitle = (approval) => {
    // Try multiple sources for title
    return approval.suggested_title 
      || approval.analysis?.headline_candidate 
      || approval.headline_candidate
      || (approval.tweet_text ? approval.tweet_text.substring(0, 80) + '...' : 'Sin título');
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500">Cargando Monitor X...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* New Header Design */}
      <div className="bg-white rounded-xl border shadow-sm">
        {/* Top Row - Title and Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border-b">
          <div className="flex items-center gap-3 mb-3 md:mb-0">
            <Twitter className="w-6 h-6 text-blue-500" />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold">Monitor de X</h2>
                {highRelevanceCount > 0 && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 border border-amber-300 rounded-full animate-pulse" title={`${highRelevanceCount} noticias con relevancia alta (>50) esperando aprobación`}>
                    <Bell className="w-3.5 h-3.5 text-amber-600" />
                    <span className="text-xs font-bold text-amber-700">{highRelevanceCount}</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {stats?.monitored_accounts?.length || 0} cuentas monitoreadas
              </p>
            </div>
          </div>
          
          {/* Scraping Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Scraping Status */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <div className={`w-2 h-2 rounded-full ${scrapingActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
              <span className="text-sm font-medium">
                {scrapingActive ? 'Scraping Activo' : 'Scraping Pausado'}
              </span>
            </div>
            
            {/* Interval Selector */}
            <select
              value={scrapingInterval}
              onChange={(e) => handleChangeInterval(parseInt(e.target.value))}
              disabled={changingInterval}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={5}>Cada 5 min</option>
              <option value={10}>Cada 10 min</option>
              <option value={15}>Cada 15 min</option>
              <option value={30}>Cada 30 min</option>
              <option value={60}>Cada 1 hora</option>
              <option value={120}>Cada 2 horas</option>
            </select>
            
            {/* Pause/Resume Button */}
            <Button
              onClick={handleToggleScraping}
              variant={scrapingActive ? "outline" : "default"}
              className={scrapingActive ? "border-orange-300 text-orange-600 hover:bg-orange-50" : "bg-green-600 hover:bg-green-700 text-white"}
            >
              {scrapingActive ? (
                <>
                  <span className="mr-2">⏸</span> Pausar
                </>
              ) : (
                <>
                  <span className="mr-2">▶</span> Reanudar
                </>
              )}
            </Button>
            
            {/* Run Now Button */}
            <Button
              onClick={handleRunMonitor}
              disabled={running}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {running ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Ejecutando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Ejecutar Ahora
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4">
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-700">{stats?.stats?.pending_approvals || 0}</p>
            <p className="text-sm text-yellow-600">Por Aprobar</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-700">{stats?.stats?.active_stories || 0}</p>
            <p className="text-sm text-green-600">Historias</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-700">{stats?.stats?.total_tweets_ingested?.toLocaleString() || 0}</p>
            <p className="text-sm text-blue-600">Tweets Totales</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-700">{generatingItems.length}</p>
            <p className="text-sm text-purple-600">En Redacción</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-700">{formatNextScraping(nextScrapingTime)}</p>
            <p className="text-sm text-gray-600">Próximo Scraping</p>
          </div>
        </div>
      </div>

      {/* Run Result Message */}
      {runResult && (
        <div className={`p-4 rounded-xl ${runResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
          <p className={`font-medium ${runResult.success ? 'text-green-800' : 'text-red-800'}`}>
            {runResult.message}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="flex overflow-x-auto border-b scrollbar-hide">
          <button
            onClick={() => setActiveView('approvals')}
            className={`flex-shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeView === 'approvals' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-1">
              Por Aprobar
              {highRelevanceCount > 0 && (
                <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-amber-500 rounded-full animate-pulse">
                  {highRelevanceCount > 9 ? '9+' : highRelevanceCount}
                </span>
              )}
              <span className="text-gray-400">({stats?.stats?.pending_approvals || 0})</span>
            </span>
          </button>
          <button
            onClick={() => setActiveView('redaccion')}
            className={`flex-shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeView === 'redaccion' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            En Redacción ({generatingItems.length})
          </button>
          <button
            onClick={() => setActiveView('stories')}
            className={`flex-shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeView === 'stories' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Historias ({stats?.stats?.active_stories || 0})
          </button>
          <button
            onClick={() => setActiveView('accounts')}
            className={`flex-shrink-0 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeView === 'accounts' 
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Cuentas ({stats?.monitored_accounts?.length || 0})
          </button>
        </div>
      </div>

      {/* APPROVALS VIEW */}
      {activeView === 'approvals' && (
        <div className="bg-white rounded-xl border shadow-sm">
          {/* Filters and Page Size */}
          <div className="p-4 border-b">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <Filter className="w-5 h-5" />
                <span className="font-medium">Filtros</span>
                <ChevronRight className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-90' : ''}`} />
              </button>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Mostrando {approvals.length} de {pagination.total_count} tweets
                </span>
                <select
                  value={pageLimit}
                  onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-900 bg-white"
                >
                  <option value={50}>50 tweets</option>
                  <option value={100}>100 tweets</option>
                  <option value={200}>200 tweets</option>
                  <option value={0}>Todos</option>
                </select>
              </div>
            </div>
            
            {showFilters && (
              <div className="mt-4 flex flex-wrap gap-4">
                <select
                  value={filters.username}
                  onChange={(e) => setFilters({...filters, username: e.target.value})}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                >
                  <option value="">Todas las cuentas</option>
                  {stats?.monitored_accounts?.map(acc => (
                    <option key={acc.username} value={acc.username}>@{acc.username}</option>
                  ))}
                </select>
                
                <select
                  value={filters.sort_order}
                  onChange={(e) => setFilters({...filters, sort_order: e.target.value})}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                >
                  <option value="desc">Más recientes</option>
                  <option value="asc">Más antiguos</option>
                </select>
                
                <Button onClick={() => { setCurrentPage(1); fetchData(); }}>
                  Aplicar
                </Button>
              </div>
            )}
          </div>

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <div className="p-4 bg-blue-50 border-b flex flex-wrap items-center gap-4">
              <span className="font-medium text-blue-800">
                {selectedIds.length} seleccionados
              </span>
              <Button
                onClick={() => handleBulkAction('APPROVE')}
                disabled={bulkProcessing}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                {bulkProcessing ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-1" />
                )}
                Aprobar Todos
              </Button>
              <Button
                onClick={() => handleBulkAction('REJECT')}
                disabled={bulkProcessing}
                variant="destructive"
                size="sm"
              >
                <X className="w-4 h-4 mr-1" />
                Rechazar Todos
              </Button>
              <Button
                onClick={() => setSelectedIds([])}
                variant="ghost"
                size="sm"
              >
                Cancelar
              </Button>
            </div>
          )}

          {/* Approvals List */}
          <div className="divide-y">
            {/* Select All Header */}
            <div className="p-4 bg-gray-50 flex items-center gap-4">
              <input
                type="checkbox"
                checked={selectedIds.length === approvals.length && approvals.length > 0}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded"
              />
              <span className="text-sm text-gray-600">
                {pagination.total_count.toLocaleString()} tweets pendientes 
                {pageLimit > 0 && ` (Página ${currentPage} de ${pagination.total_pages})`}
                {pageLimit === 0 && ' (Mostrando todos)'}
              </span>
            </div>

            {approvals.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No hay tweets pendientes de aprobación</p>
              </div>
            ) : (
              approvals.map(approval => (
                <div 
                  key={approval.approval_id}
                  className={`p-3 sm:p-4 hover:bg-gray-50 ${selectedIds.includes(approval.approval_id) ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    {/* Checkbox - hidden on mobile, shown on desktop */}
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(approval.approval_id)}
                      onChange={() => toggleSelect(approval.approval_id)}
                      className="hidden sm:block w-5 h-5 rounded mt-1 flex-shrink-0"
                    />
                    
                    <div className="flex-1 min-w-0">
                      {/* Header row - mobile optimized */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-blue-100 text-blue-800 text-xs">
                          @{approval.tweet_author || approval.account}
                        </Badge>
                        {(approval.analysis?.newsworthiness_score || approval.newsworthiness_score) && (
                          <Badge variant="outline" className={`text-xs ${
                            (approval.analysis?.newsworthiness_score || approval.newsworthiness_score) >= 80 ? 'bg-green-100 text-green-800' :
                            (approval.analysis?.newsworthiness_score || approval.newsworthiness_score) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {approval.analysis?.newsworthiness_score || approval.newsworthiness_score}
                          </Badge>
                        )}
                        <span className="text-xs text-gray-500">{formatDate(approval.created_at)}</span>
                      </div>
                      
                      <h4 className="font-medium text-gray-900 mb-1 text-sm sm:text-base">
                        {getApprovalTitle(approval)}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                        {approval.tweet_text || 'Sin contenido'}
                      </p>
                      
                      {approval.tweet_url && (
                        <a 
                          href={approval.tweet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Ver tweet
                        </a>
                      )}
                    </div>
                    
                    {/* Action buttons - full width on mobile */}
                    <div className="flex gap-2 mt-2 sm:mt-0 sm:flex-shrink-0">
                      <Button
                        onClick={() => setSelectedApproval(approval)}
                        variant="ghost"
                        size="sm"
                        className="flex-1 sm:flex-none"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleProcess(approval.approval_id, 'APPROVE')}
                        disabled={processingId === approval.approval_id}
                        className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white"
                        size="sm"
                      >
                        {processingId === approval.approval_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        onClick={() => handleProcess(approval.approval_id, 'REJECT')}
                        disabled={processingId === approval.approval_id}
                        variant="destructive"
                        size="sm"
                        className="flex-1 sm:flex-none"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && pageLimit > 0 && (
            <div className="p-4 border-t flex items-center justify-center gap-2">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_prev}
                variant="outline"
                size="sm"
              >
                Anterior
              </Button>
              <span className="px-4 text-sm text-gray-600">
                Página {currentPage} de {pagination.total_pages}
              </span>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next}
                variant="outline"
                size="sm"
              >
                Siguiente
              </Button>
            </div>
          )}
        </div>
      )}

      {/* EN REDACCIÓN VIEW - NEW TAB */}
      {activeView === 'redaccion' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Edit3 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold">Artículos en Redacción</h3>
              {generatingItems.length > 0 && (
                <Badge className="bg-blue-100 text-blue-800 animate-pulse">
                  {generatingItems.length} en proceso
                </Badge>
              )}
            </div>
            <Button onClick={fetchGenerating} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>

          <div className="divide-y">
            {generatingItems.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Edit3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h4 className="text-lg font-medium mb-2">No hay artículos en redacción</h4>
                <p className="text-sm">Cuando apruebes tweets, aparecerán aquí con su progreso de generación.</p>
                <Button 
                  onClick={() => setActiveView('approvals')} 
                  variant="outline" 
                  className="mt-4"
                >
                  Ir a Aprobaciones
                </Button>
              </div>
            ) : (
              generatingItems.map((item, index) => {
                const elapsedSeconds = getElapsedSeconds(item);
                // Live progress derived from elapsed time so the bar advances
                // every second; never goes below the backend-reported progress.
                const liveProgress = Math.max(
                  item.progress || 0,
                  Math.min(95, Math.floor((elapsedSeconds / 120) * 100))
                );
                return (
                <div key={item.approval_id || index} className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-blue-100 text-blue-800">
                          @{item.tweet_author || item.account || 'desconocido'}
                        </Badge>
                        <Badge className="bg-blue-500 text-white animate-pulse">
                          {liveProgress}%
                        </Badge>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        {item.suggested_title || item.analysis?.headline_candidate || 'Generando título...'}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {item.tweet_text || 'Procesando contenido...'}
                      </p>
                    </div>
                    <div className="ml-4 text-right">
                      <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-700 ${getProgressColor(liveProgress)}`}
                        style={{ width: `${liveProgress}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="font-medium">{getProgressStage(liveProgress)}</span>
                      <span>
                        {(item.generation_started_at || item.created_at || item.elapsed_seconds)
                          ? `${Math.floor(elapsedSeconds / 60)}:${(elapsedSeconds % 60).toString().padStart(2, '0')} transcurrido`
                          : 'Iniciando...'
                        }
                      </span>
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </div>

          {generatingItems.length > 0 && (
            <div className="p-4 bg-blue-50 border-t">
              <p className="text-sm text-blue-700 text-center">
                <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" />
                Los artículos se están generando automáticamente. Esta página se actualiza cada 3 segundos.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ACCOUNTS VIEW */}
      {activeView === 'accounts' && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <h3 className="text-lg font-bold mb-4">Cuentas Monitoreadas</h3>
          
          {/* Add Account */}
          <div className="flex gap-2 mb-6">
            <Input
              placeholder="@usuario"
              value={newAccount}
              onChange={(e) => setNewAccount(e.target.value)}
              className="max-w-xs"
            />
            <Button
              onClick={handleAddAccount}
              disabled={addingAccount || !newAccount.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2"
            >
              {addingAccount ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar
                </>
              )}
            </Button>
          </div>

          {/* Accounts List */}
          <div className="space-y-3">
            {stats?.monitored_accounts?.map(account => (
              <div 
                key={account.username}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Twitter className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">@{account.username}</span>
                  {account.auto_publish && (
                    <Badge className="bg-green-100 text-green-800">Auto-publicar</Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleAutoPublish(account.username, account.auto_publish)}
                    className="p-2 hover:bg-gray-200 rounded-lg"
                    title={account.auto_publish ? 'Desactivar auto-publicar' : 'Activar auto-publicar'}
                  >
                    {account.auto_publish ? (
                      <ToggleRight className="w-6 h-6 text-green-600" />
                    ) : (
                      <ToggleLeft className="w-6 h-6 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => handleRemoveAccount(account.username)}
                    className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
            
            {(!stats?.monitored_accounts || stats.monitored_accounts.length === 0) && (
              <div className="text-center text-gray-500 py-8">
                <Twitter className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No hay cuentas monitoreadas</p>
                <p className="text-sm">Agrega una cuenta para comenzar</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STORIES VIEW */}
      {activeView === 'stories' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-bold">Historias Activas ({stories.length})</h3>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
          
          <div className="divide-y">
            {stories.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No hay historias activas</p>
                <p className="text-sm mt-2">Las historias se crean cuando apruebas tweets desde "Por Aprobar"</p>
              </div>
            ) : (
              stories.map(story => {
                const isFailed = isStoryFailed(story);
                return (
                <div key={story.story_id} className={`p-4 hover:bg-gray-50 ${isFailed ? 'bg-red-50 border-l-4 border-red-400' : ''}`}>
                  <div className="flex items-start gap-4">
                    {/* Story image thumbnail or error icon */}
                    {isFailed ? (
                      <div className="w-28 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-red-100 flex items-center justify-center">
                        <AlertCircle className="w-8 h-8 text-red-500" />
                      </div>
                    ) : story.cover_image ? (
                      <div className="w-28 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {/* object-contain + caja flex: muestra la imagen completa
                            sin distorsionar ni recortar, sea cual sea su proporción */}
                        <img
                          src={story.cover_image.startsWith('data:') || story.cover_image.startsWith('http')
                            ? story.cover_image
                            : `${process.env.REACT_APP_BACKEND_URL}${story.cover_image}`}
                          alt={story.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : null}
                    
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium line-clamp-1 ${isFailed ? 'text-red-800' : 'text-gray-900'}`}>
                        {story.title || 'Sin título'}
                      </h4>
                      {/* Show error message or summary */}
                      <p className={`text-sm mt-1 line-clamp-2 ${isFailed ? 'text-red-600' : 'text-gray-600'}`}>
                        {isFailed 
                          ? '⚠️ Error en la generación del artículo. Puedes reintentar la generación.'
                          : (story.summary ? story.summary.replace(/<[^>]+>/g, '').substring(0, 150) : 'Sin resumen')
                        }
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{story.tweet_count || 0} tweets</span>
                        <span>{story.article_count || 0} artículos</span>
                        <span>{formatDate(story.created_at)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Error/Status Badge */}
                      {isFailed ? (
                        <Badge className="bg-red-100 text-red-800">
                          Error
                        </Badge>
                      ) : (
                        <Badge className={
                          story.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                          story.status === 'DEVELOPING' ? 'bg-yellow-100 text-yellow-800' :
                          story.status === 'REGENERATING' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {story.status === 'REGENERATING' ? 'Regenerando...' : story.status}
                        </Badge>
                      )}
                      
                      {/* Retry button for failed stories */}
                      {isFailed && (
                        <Button
                          onClick={() => handleRetryStory(story.story_id)}
                          disabled={retryingStoryId === story.story_id || story.status === 'REGENERATING'}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          size="sm"
                          title="Reintentar generación"
                          data-testid={`retry-story-${story.story_id}`}
                        >
                          {retryingStoryId === story.story_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Reintentar
                            </>
                          )}
                        </Button>
                      )}
                      
                      {/* Delete button for failed stories */}
                      {isFailed && (
                        <Button
                          onClick={() => handleDeleteStory(story.story_id, story.title)}
                          disabled={deletingStoryId === story.story_id}
                          variant="destructive"
                          size="sm"
                          title="Eliminar historia"
                          data-testid={`delete-story-${story.story_id}`}
                        >
                          {deletingStoryId === story.story_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4 mr-1" />
                              Eliminar
                            </>
                          )}
                        </Button>
                      )}
                      
                      {/* Action buttons */}
                      <Button
                        onClick={() => {
                          setSelectedStory(story);
                          setEditingStory(false);
                        }}
                        variant="ghost"
                        size="sm"
                        title="Ver detalles"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedStory(story);
                          setStoryFormData({
                            title: story.title || '',
                            summary: story.summary?.replace(/<[^>]+>/g, '') || '',
                            status: story.status || 'ACTIVE'
                          });
                          setEditingStory(true);
                        }}
                        variant="ghost"
                        size="sm"
                        title="Editar"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )})
            )}
          </div>
        </div>
      )}
      
      {/* Story Detail/Edit Modal */}
      {selectedStory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">
                  {editingStory ? 'Editar Historia' : 'Detalle de Historia'}
                </h3>
                <div className="flex items-center gap-2">
                  {!editingStory && (
                    <Button
                      onClick={() => {
                        setStoryFormData({
                          title: selectedStory.title || '',
                          summary: selectedStory.summary?.replace(/<[^>]+>/g, '') || '',
                          status: selectedStory.status || 'ACTIVE'
                        });
                        setEditingStory(true);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedStory(null);
                      setEditingStory(false);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Cover Image */}
              {selectedStory.cover_image && (
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-gray-100">
                  <img 
                    src={selectedStory.cover_image.startsWith('data:') || selectedStory.cover_image.startsWith('http') 
                      ? selectedStory.cover_image 
                      : `${process.env.REACT_APP_BACKEND_URL}${selectedStory.cover_image}`}
                    alt={selectedStory.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {editingStory ? (
                /* Edit Form */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                    <Input
                      value={storyFormData.title}
                      onChange={(e) => setStoryFormData({...storyFormData, title: e.target.value})}
                      placeholder="Título de la historia"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Resumen</label>
                    <textarea
                      value={storyFormData.summary}
                      onChange={(e) => setStoryFormData({...storyFormData, summary: e.target.value})}
                      placeholder="Resumen de la historia"
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                    <select
                      value={storyFormData.status}
                      onChange={(e) => setStoryFormData({...storyFormData, status: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="ACTIVE">Activo</option>
                      <option value="DEVELOPING">En desarrollo</option>
                      <option value="ARCHIVED">Archivado</option>
                    </select>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Título</label>
                    <h4 className="text-lg font-semibold text-gray-900">{selectedStory.title}</h4>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Estado</label>
                    <div className="mt-1">
                      <Badge className={
                        selectedStory.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                        selectedStory.status === 'DEVELOPING' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {selectedStory.status === 'ACTIVE' ? 'Activo' : 
                         selectedStory.status === 'DEVELOPING' ? 'En desarrollo' : 
                         selectedStory.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Resumen / Contenido</label>
                    <div 
                      className="mt-1 text-gray-700 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: selectedStory.summary || selectedStory.content || '<p class="text-gray-400">Sin contenido</p>' 
                      }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Tweets relacionados</label>
                      <p className="text-gray-900">{selectedStory.tweet_count || 0}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Artículos generados</label>
                      <p className="text-gray-900">{selectedStory.article_count || 0}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Creado</label>
                      <p className="text-gray-900">{formatDate(selectedStory.created_at)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">ID</label>
                      <p className="text-gray-500 text-xs font-mono">{selectedStory.story_id}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
              {editingStory ? (
                <>
                  <Button
                    onClick={() => setEditingStory(false)}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={async () => {
                      setSavingStory(true);
                      try {
                        await api.updateStory(selectedStory.story_id, storyFormData);
                        setRunResult({
                          success: true,
                          message: '✅ Historia actualizada correctamente',
                          details: null
                        });
                        setTimeout(() => setRunResult(null), 3000);
                        
                        // Update local state
                        setStories(prev => prev.map(s => 
                          s.story_id === selectedStory.story_id 
                            ? {...s, ...storyFormData}
                            : s
                        ));
                        setSelectedStory({...selectedStory, ...storyFormData});
                        setEditingStory(false);
                      } catch (error) {
                        console.error('Error updating story:', error);
                        setRunResult({
                          success: false,
                          message: '❌ Error al actualizar la historia',
                          details: error.message
                        });
                        setTimeout(() => setRunResult(null), 5000);
                      } finally {
                        setSavingStory(false);
                      }
                    }}
                    disabled={savingStory}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {savingStory ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Guardar Cambios
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setSelectedStory(null);
                    setEditingStory(false);
                  }}
                  variant="outline"
                >
                  Cerrar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approval Detail Modal */}
      {selectedApproval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Detalle del Tweet</h3>
                <button
                  onClick={() => setSelectedApproval(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Cuenta</label>
                <p className="text-blue-600">@{selectedApproval.tweet_author || selectedApproval.account}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Título Sugerido</label>
                <p className="font-medium">{getApprovalTitle(selectedApproval)}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Tweet Original</label>
                <p className="text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{selectedApproval.tweet_text}</p>
              </div>
              
              {selectedApproval.analysis && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Análisis</label>
                  <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm">
                    <p><span className="font-medium">Tema:</span> {selectedApproval.analysis.topic_label || 'N/A'}</p>
                    <p><span className="font-medium">Puntuación:</span> {selectedApproval.analysis.newsworthiness_score || 'N/A'}</p>
                    <p><span className="font-medium">Qué pasó:</span> {selectedApproval.analysis.what_happened || 'N/A'}</p>
                  </div>
                </div>
              )}
              
              {selectedApproval.tweet_url && (
                <a 
                  href={selectedApproval.tweet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ver tweet en X
                </a>
              )}
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <Button
                onClick={() => setSelectedApproval(null)}
                variant="outline"
              >
                Cerrar
              </Button>
              <Button
                onClick={() => handleProcess(selectedApproval.approval_id, 'REJECT')}
                variant="destructive"
              >
                <X className="w-4 h-4 mr-2" />
                Rechazar
              </Button>
              <Button
                onClick={() => handleProcess(selectedApproval.approval_id, 'APPROVE')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Aprobar y Generar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XMonitorManager;
